import { EMAIL_MAX_LENGTH, NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@sagot/constants";
import { NAME_MIN_LENGTH } from "./register-constants";

/** The mockup's `isEmail`. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const LETTER = /[a-z]/i;
const DIGIT = /\d/;
const SYMBOL = /[^a-z0-9]/i;
const STRONG_LENGTH = 14;

export function isEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length <= EMAIL_MAX_LENGTH && EMAIL_PATTERN.test(trimmed);
}

export type PasswordScore = 0 | 1 | 2 | 3 | 4;

const hasMix = (value: string): boolean => LETTER.test(value) && DIGIT.test(value);

/** The mockup's `pwScore`: 0 when empty, else 1 to 4 (10+ characters, letters with digits, a symbol, 14+ characters). */
export function registerPasswordScore(value: string): PasswordScore {
  if (!value) return 0;
  let score = 0;
  if (value.length >= PASSWORD_MIN_LENGTH) score += 1;
  if (hasMix(value)) score += 1;
  if (SYMBOL.test(value)) score += 1;
  if (value.length >= STRONG_LENGTH) score += 1;
  return Math.max(1, score) as PasswordScore;
}

export type PasswordHelp =
  | { kind: "empty" }
  | { kind: "short"; missing: number }
  | { kind: "mix" }
  | { kind: "score"; score: 1 | 2 | 3 | 4 };

/** What the line under the strength bar says (the mockup's `aboutCheck`). */
export function passwordHelp(value: string): PasswordHelp {
  if (!value) return { kind: "empty" };
  if (value.length < PASSWORD_MIN_LENGTH) return { kind: "short", missing: PASSWORD_MIN_LENGTH - value.length };
  if (!hasMix(value)) return { kind: "mix" };
  return { kind: "score", score: Math.max(1, registerPasswordScore(value)) as 1 | 2 | 3 | 4 };
}

export function isPasswordOk(value: string): boolean {
  return value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH && hasMix(value);
}

export function isNameOk(value: string): boolean {
  const length = value.trim().length;
  return length >= NAME_MIN_LENGTH && length <= NAME_MAX_LENGTH;
}

export function isOrgNameOk(orgName: string): boolean {
  const length = orgName.trim().length;
  return length > 0 && length <= NAME_MAX_LENGTH;
}

export function isOrgOk(orgName: string, industry: string | null): boolean {
  return isOrgNameOk(orgName) && industry !== null;
}
